import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useFlashcards } from '../../context/FlashcardContext';
import { useTheme } from '../../context/ThemeContext';
import SwipeCard from '../SwipeCard';
import { playSound } from '../Audio';
import { FeedbackOverlay, ProgressBar, ScoreDisplay, Rewards } from '../GameElements';
import SettingsDialog from '../SettingsDialog';
import WelcomeScreen from '../WelcomeScreen';
import { getReviewStyles } from './styles';
import { getNextBatchOfCards } from './utils';

const CreateCard = lazy(() => import('../CreateCard'));

export default function ReviewScreen() {
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem('welcomeShown');
  });

  const handleCloseWelcome = () => {
    localStorage.setItem('welcomeShown', 'true');
    setShowWelcome(false);
  };

  const { 
    allFlashcards, 
    flashcards, 
    score, 
    updateScore, 
    markCardAsReviewed, 
    isMuted, 
    toggleMute, 
    groups, 
    activeGroupId, 
    setActiveGroupId 
  } = useFlashcards();

  const [sessionCards, setSessionCards] = useState([]);
  const [previousBatch, setPreviousBatch] = useState([]);
  const [cardsReviewed, setCardsReviewed] = useState(() => {
    return allFlashcards.filter(card => card.groupId === activeGroupId && card.reviewed).length;
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showManageCards, setShowManageCards] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastCorrect, setLastCorrect] = useState(null);
  const [isCardVisible, setCardVisible] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const totalCardsInGroup = flashcards.length;

  useEffect(() => {
    if (!activeGroupId && groups.length > 0) {
      setActiveGroupId(groups[0].id);
    }
  }, [activeGroupId, groups, setActiveGroupId]);

  useEffect(() => {
    if (activeGroupId) {
      const reviewedCount = allFlashcards.filter(card => 
        card.groupId === activeGroupId && card.reviewed
      ).length;
      setCardsReviewed(reviewedCount);
    }
  }, [activeGroupId, allFlashcards]);

  const { colors } = useTheme();
  const styles = getReviewStyles(colors, score);

  const handleSwipe = async (isCorrect) => {
    if (!isMuted) {
      playSound(isCorrect ? 'correct' : 'incorrect');
    }
    setLastCorrect(isCorrect);
    setShowFeedback(true);
    updateScore(isCorrect);
    markCardAsReviewed(currentCard.id);
    setCardsReviewed(prev => prev + 1);

    setIsTransitioning(true);
    await new Promise(resolve => setTimeout(resolve, 300));
    setShowFeedback(false);
    setCardVisible(false);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (currentIndex < sessionCards.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setPreviousBatch(sessionCards);
      const nextBatch = getNextBatchOfCards(flashcards, sessionCards);
      setSessionCards(nextBatch);
      setCurrentIndex(0);
    }
    
    await new Promise(resolve => setTimeout(resolve, 10));
    setCardVisible(true);
    setIsTransitioning(false);
  };

  const handleGroupChange = (e) => {
    const newGroupId = e.target.value;
    setActiveGroupId(newGroupId);
    setPreviousBatch([]);
    setCardsReviewed(0);
    setCurrentIndex(0);
    setSessionCards([]); // This will trigger our useEffect to load new cards
  };

  const currentCard = sessionCards[currentIndex];

  useEffect(() => {
    if (flashcards.length > 0) {
      if (sessionCards.length === 0 || activeGroupId !== sessionCards[0]?.groupId) {
        // Initial load or group change
        const nextBatch = getNextBatchOfCards(flashcards);
        if (nextBatch.length > 0) {
          setSessionCards(nextBatch);
          setPreviousBatch([]);
          setCurrentIndex(0);
        }
      } else if (currentIndex >= sessionCards.length) {
        // Need new batch in same group
        const nextBatch = getNextBatchOfCards(flashcards, sessionCards);
        if (nextBatch.length > 0) {
          setPreviousBatch(sessionCards);
          setSessionCards(nextBatch);
          setCurrentIndex(0);
        }
      }
    }
  }, [currentIndex, sessionCards, flashcards, activeGroupId]);

  const mainContent = !flashcards.length ? (
    <div style={styles.container}>
      <p>No cards in this group...</p>
      <button onClick={() => setShowManageCards(true)}>Manage Cards</button>
    </div>
  ) : !currentCard ? (
    <div style={styles.container}>
      <p>Loading...</p>
    </div>
  ) : (
    <div style={styles.container}>
      <FeedbackOverlay isCorrect={lastCorrect} show={showFeedback} />

      <>
        <div style={styles.scoreContainer}>
          <ScoreDisplay score={score.total} className={styles.score} />
          <div style={styles.scoreDetailsContainer}>
            <div style={styles.scoreDetail}>
              [{score.right}<span style={styles.checkMark}>✓</span> {score.wrong}<span style={styles.wrongMark}>✗</span>]
            </div>
          </div>
          <Rewards score={score.total} />
        </div>
        <ProgressBar current={cardsReviewed} total={totalCardsInGroup} />
      </>

      {!isTransitioning && (
        <SwipeCard
          word={currentCard.question}
          answer={currentCard.answer}
          incorrect={currentCard.incorrect}
          onSwipe={handleSwipe}
          isVisible={isCardVisible}
        />
      )}
    </div>
  );

  return (
    <>
      {showManageCards ? (
        <Suspense fallback={<div style={styles.container}>Loading...</div>}>
          <CreateCard onClose={() => setShowManageCards(false)} />
        </Suspense>
      ) : (
        <>
          {showWelcome && <WelcomeScreen onClose={handleCloseWelcome} />}
          {mainContent}
        </>
      )}
      <button
        onClick={() => setShowSettings(true)}
        style={styles.controlButton}
        title="Menu"
      >
        ⚙️
      </button>

      <SettingsDialog
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        activeGroup={activeGroupId}
        groups={groups}
        onGroupChange={handleGroupChange}
        onManageCards={() => {
          setShowSettings(false);
          setShowManageCards(true);
        }}
        isMuted={isMuted}
        onToggleMute={toggleMute}
        onInstallClick={() => {
          setShowSettings(false);
          const installEvent = new CustomEvent('showInstallPrompt');
          window.dispatchEvent(installEvent);
        }}
      />
    </>
  );
}
